import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const ClipboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);
const AwardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);
const AwardLargeIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);
const ClipboardLargeIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const CurrencyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const VideoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const UserCircleIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

/* ─── CIRCUIT BG ──────────────────────────────────────────── */
const CircuitBg = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0, opacity: 0.07 }}>
    <svg viewBox="0 0 1400 800" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%' }}>
      <g stroke={V2} strokeWidth="0.9" fill="none">
        <line x1="0" y1="160" x2="320" y2="160" /><line x1="380" y1="160" x2="680" y2="160" />
        <line x1="980" y1="120" x2="1400" y2="120" />
        <line x1="0" y1="420" x2="210" y2="420" /><line x1="570" y1="420" x2="880" y2="420" />
        <line x1="320" y1="0" x2="320" y2="260" /><line x1="680" y1="120" x2="680" y2="260" />
        <circle cx="320" cy="160" r="5" fill={V1} opacity="0.9" />
        <circle cx="680" cy="160" r="4" fill={V2} opacity="0.9" />
        <circle cx="210" cy="420" r="4" fill={V2} opacity="0.9" />
        <circle cx="570" cy="420" r="5" fill={V1} opacity="0.9" />
      </g>
    </svg>
  </div>
);

const statusConfig = {
  pending: { label: 'Pending Review', color: 'var(--warning)', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.3)' },
  payment_verified: { label: 'Payment Verified', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  approved: { label: 'Approved', color: 'var(--success)', bg: 'rgba(13,186,121,0.12)', border: 'rgba(13,186,121,0.3)' },
  rejected: { label: 'Rejected', color: 'var(--danger)', bg: 'rgba(240,65,85,0.12)', border: 'rgba(240,65,85,0.3)' },
  completed: { label: 'Completed', color: 'var(--badge-pill-text)', bg: 'var(--badge-pill-bg)', border: 'var(--badge-pill-border)' },
};

const microStatusConfig = {
  pending_payment: { label: 'Payment Pending', color: 'var(--warning)', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.3)' },
  in_progress: { label: 'Task Unlocked', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  under_review: { label: 'Under Review', color: '#F5A623', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.3)' },
  completed: { label: 'Completed', color: 'var(--success)', bg: 'rgba(13,186,121,0.12)', border: 'rgba(13,186,121,0.3)' },
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [microinternships, setMicrointernships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('applications');

  useEffect(() => {
    Promise.all([
      API.get('/applications/my').catch(() => ({ data: [] })),
      API.get('/certificates/my').catch(() => ({ data: [] })),
      API.get('/microinternships/my').catch(() => ({ data: [] })),
    ]).then(([appRes, certRes, microRes]) => {
      setApplications(appRes.data || []);
      setCertificates(certRes.data || []);
      setMicrointernships(microRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const downloadCert = async (filePath, certId) => {
    if (!filePath || !filePath.startsWith('http')) {
      toast.error('Certificate file not available. Please contact support.');
      return;
    }
    try {
      toast.loading('Preparing download...', { id: 'cert-dl' });
      const response = await fetch(filePath);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ThiranX_Certificate_${certId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!', { id: 'cert-dl' });
    } catch {
      toast.error('Download failed. Try again.', { id: 'cert-dl' });
    }
  };



  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>

      {/* ══ HERO / HEADER ═══════════════════════════════════ */}
      <section style={{
        padding: '120px 0 60px', position: 'relative', overflow: 'hidden',
        background: 'var(--bg)',
      }}>
        <CircuitBg />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: `radial-gradient(circle, var(--hero-orb) 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: `linear-gradient(135deg, ${V4}, ${V3})`,
              border: `2px solid ${V1}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: V2, flexShrink: 0,
            }}>
              <UserCircleIcon />
            </div>
            <div>
              <div style={{ fontSize: 11.5, color: V2, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                Student Portal
              </div>
              <h1 style={{
                fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 4,
                fontFamily: "'Inter',sans-serif",
              }}>
                Welcome back, <span style={{
                  background: `linear-gradient(135deg,${V1},${V2})`,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>{user.name}</span>
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 14.5 }}>
                Track your applications, download certificates, and continue learning.
              </p>
            </div>
          </div>

          {/* Quick stats */}
          <div style={{ display: 'flex', gap: 16, marginTop: 36, flexWrap: 'wrap' }}>
            {[
              { label: 'Applications', value: applications.length },
              { label: 'Microinterns', value: microinternships.length },
              { label: 'Certificates', value: certificates.length },
            ].map((s, i) => (
              <div key={i} style={{
                background: 'var(--card-solid)', border: '1px solid var(--border)',
                borderRadius: 14, padding: '16px 24px', minWidth: 130,
                boxShadow: 'var(--shadow-card)',
              }}>
                <div style={{
                  fontFamily: "'Inter',sans-serif", fontWeight: 900,
                  fontSize: 28, color: 'var(--text)', lineHeight: 1,
                }}>{s.value}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TABS + CONTENT ══════════════════════════════════ */}
      <section style={{ padding: '0 0 100px', background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
        <div className="container" style={{ paddingTop: 48 }}>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 36, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'applications', icon: <ClipboardIcon />, label: `My Applications (${applications.length})` },
              { key: 'microinternships', icon: <BriefcaseIcon />, label: `Microinternships (${microinternships.length})` },
              { key: 'certificates', icon: <AwardIcon />, label: `My Certificates (${certificates.length})` },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '11px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontFamily: "'Satoshi','DM Sans',sans-serif", fontWeight: 700, fontSize: 14,
                  background: tab === t.key ? `linear-gradient(135deg,${V1},${V3})` : 'var(--card-solid)',
                  color: tab === t.key ? '#fff' : 'var(--muted)',
                  boxShadow: tab === t.key ? `0 4px 20px ${V1}44` : 'var(--shadow-card)',
                  border: tab === t.key ? 'none' : '1px solid var(--border)',
                  transition: 'all 0.2s',
                }}
              >
                {t.icon}{t.label}
              </button>
            ))}
            <Link to="/courses" style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginLeft: 'auto', padding: '11px 20px', borderRadius: 10,
              background: 'transparent', border: '1.5px solid var(--border-strong)',
              color: V2, fontWeight: 700, fontSize: 14,
              fontFamily: "'Satoshi','DM Sans',sans-serif",
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = V1; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = V1; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = V2; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
            >
              <PlusIcon /> Apply New Course
            </Link>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '80px 0', fontSize: 15 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${V1}33`, borderTopColor: V1, animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              Loading your data...
            </div>
          ) : (
            <>
              {/* ── APPLICATIONS TAB ─────────────────────── */}
              {tab === 'applications' && (
                applications.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '72px 24px',
                    background: 'var(--card-solid)', border: '1px solid var(--border)',
                    borderRadius: 20,
                  }}>
                    <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                      <ClipboardLargeIcon />
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, fontFamily: "'Inter',sans-serif" }}>No Applications Yet</h3>
                    <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 15, lineHeight: 1.6 }}>Browse courses and apply to get started on your learning journey!</p>
                    <Link to="/courses" className="btn btn-primary">Browse Courses</Link>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {applications.map(app => {
                      const status = statusConfig[app.status] || statusConfig.pending;
                      return (
                        <div key={app._id} style={{
                          background: 'var(--card-solid)', border: '1px solid var(--border)',
                          borderRadius: 20, padding: '24px 28px',
                          display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center',
                          boxShadow: 'var(--shadow-card)',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                              <h3 style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>{app.course?.title}</h3>
                              <span style={{
                                display: 'inline-flex', padding: '4px 12px', borderRadius: 999,
                                fontSize: 11.5, fontWeight: 700,
                                color: status.color, background: status.bg, border: `1px solid ${status.border}`,
                              }}>{status.label}</span>
                            </div>
                            <div style={{ color: 'var(--muted)', fontSize: 13.5, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><ClockIcon />{app.course?.duration}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CurrencyIcon />₹{app.amountPaid?.toLocaleString()}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CalendarIcon />Applied: {new Date(app.createdAt).toLocaleDateString('en-IN')}</span>
                            </div>
                            {app.batchDate && (
                              <div style={{
                                marginTop: 12, padding: '10px 16px',
                                background: 'rgba(13,186,121,0.08)', borderRadius: 10,
                                border: '1px solid rgba(13,186,121,0.2)',
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                              }}>
                                <CalendarIcon />
                                <span style={{ color: 'var(--success)', fontSize: 13.5, fontWeight: 700 }}>
                                  Batch Date: {new Date(app.batchDate).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </span>
                              </div>
                            )}
                            {app.batchLink && (
                              <div style={{ marginTop: 10 }}>
                                <a href={app.batchLink} target="_blank" rel="noreferrer" style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 7,
                                  padding: '8px 18px', borderRadius: 8,
                                  background: `linear-gradient(135deg,${V1},${V3})`,
                                  color: '#fff', fontWeight: 700, fontSize: 13,
                                  fontFamily: "'Satoshi','DM Sans',sans-serif",
                                }}>
                                  <VideoIcon /> Join Class
                                </a>
                              </div>
                            )}
                            {app.status === 'rejected' && app.rejectionReason && (
                              <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 600 }}>
                                Reason: {app.rejectionReason}
                              </div>
                            )}
                          </div>
                          {app.certificateIssued && (
                            <button
                              onClick={() => {
                                const cert = certificates.find(c => c.certificateId === app.certificateId);
                                downloadCert(cert?.filePath, app.certificateId);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '12px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                                background: 'var(--success)', color: '#fff',
                                fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
                                fontFamily: "'Satoshi','DM Sans',sans-serif",
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                              <DownloadIcon /> Download Certificate
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* ── MICROINTERNSHIPS TAB ─────────────────── */}
              {tab === 'microinternships' && (
                microinternships.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '72px 24px',
                    background: 'var(--card-solid)', border: '1px solid var(--border)',
                    borderRadius: 20,
                  }}>
                    <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                      <BriefcaseIcon />
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, fontFamily: "'Inter',sans-serif" }}>No Microinternships Yet</h3>
                    <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 15, lineHeight: 1.6 }}>Enroll in a ₹100 microinternship to get real-world task experience!</p>
                    <Link to="/microinternships" className="btn btn-primary">View Microinternships</Link>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {microinternships.map(micro => {
                      const status = microStatusConfig[micro.status] || microStatusConfig.pending_payment;
                      return (
                        <div key={micro._id} style={{
                          background: 'var(--card-solid)', border: '1px solid var(--border)',
                          borderRadius: 20, padding: '24px 28px',
                          display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center',
                          boxShadow: 'var(--shadow-card)',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                              <h3 style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>{micro.courseName}</h3>
                              <span style={{
                                display: 'inline-flex', padding: '4px 12px', borderRadius: 999,
                                fontSize: 11.5, fontWeight: 700,
                                color: status.color, background: status.bg, border: `1px solid ${status.border}`,
                              }}>{status.label}</span>
                            </div>
                            <div style={{ color: 'var(--muted)', fontSize: 13.5, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><AwardIcon />{micro.internshipId}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CurrencyIcon />₹100 Paid</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><CalendarIcon />Enrolled: {new Date(micro.createdAt).toLocaleDateString('en-IN')}</span>
                            </div>
                          </div>
                          
                          <Link
                            to={`/microinternship-task/${micro.internshipId}?email=${micro.email}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '12px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                              background: `linear-gradient(135deg,${V1},${V3})`, color: '#fff',
                              fontWeight: 700, fontSize: 13, textDecoration: 'none',
                              fontFamily: "'Satoshi','DM Sans',sans-serif",
                              transition: 'all 0.2s',
                              boxShadow: `0 4px 16px ${V1}33`,
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                          >
                            Open Task Dashboard
                          </Link>
                          
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* ── CERTIFICATES TAB ──────────────────────── */}
              {tab === 'certificates' && (
                certificates.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '72px 24px',
                    background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20,
                  }}>
                    <div style={{ color: 'var(--muted)', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                      <AwardLargeIcon />
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, fontFamily: "'Inter',sans-serif" }}>No Certificates Yet</h3>
                    <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>Complete a course to earn your verified digital certificate!</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
                    {certificates.map(cert => (
                      <div key={cert._id} style={{
                        background: 'var(--card-solid)', border: `1px solid ${V1}44`,
                        borderRadius: 20, overflow: 'hidden',
                        boxShadow: 'var(--shadow-card)',
                        transition: 'border-color 0.25s, transform 0.25s, box-shadow 0.25s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = `${V1}88`; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 40px ${V1}22`; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = `${V1}44`; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                      >
                        {/* Top gradient strip */}
                        <div style={{ height: 6, background: `linear-gradient(90deg,${V1},${V2})` }} />
                        <div style={{ padding: '24px' }}>
                          <div style={{ color: V2, marginBottom: 14 }}><AwardLargeIcon /></div>
                          <h3 style={{ marginBottom: 6, fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', fontFamily: "'Inter',sans-serif" }}>{cert.courseName}</h3>
                          <p style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 4 }}>
                            Issued: {new Date(cert.issuedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                          <p style={{ color: 'var(--muted)', fontSize: 11.5, marginBottom: 20, fontFamily: "'Space Mono',monospace" }}>
                            {cert.certificateId}
                          </p>
                          <button
                            onClick={() => downloadCert(cert.filePath, cert.certificateId)}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              padding: '12px', borderRadius: 10, border: 'none', cursor: 'pointer',
                              background: `linear-gradient(135deg,${V1},${V3})`,
                              color: '#fff', fontWeight: 700, fontSize: 14,
                              fontFamily: "'Satoshi','DM Sans',sans-serif",
                              boxShadow: `0 4px 16px ${V1}33`,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                          >
                            <DownloadIcon /> Download PDF
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </section>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 600px) {
          .dash-app-card { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default StudentDashboard;

